import { FileUploadOutlined } from '@mui/icons-material';
import { ActionButton, FormCard, RowActionButton } from '@practium/ui';
import { Image as ImageIcon } from 'lucide-react';
import type { ChangeEvent, DragEvent, ReactNode } from 'react';
import { useRef, useState } from 'react';
import type { ProductPageMode } from '../../types';
import { ProductPhotoCarousel } from '../ProductPhotoCarousel/ProductPhotoCarousel';
import styles from './ProductImagesCard.module.css';

export interface ProductImagesCardProps {
	mode: ProductPageMode;
	preview3DSlot?: ReactNode;
	className?: string;
	onFilesChange?: (files: File[]) => void;
	files?: File[];
	imageUrls?: string[];
	onImageUrlsChange?: (images: string[]) => void;
	onRemoveImage?: (url: string) => Promise<void> | void;
}

export const ProductImagesCard = ({
	mode,
	preview3DSlot,
	className,
	onFilesChange,
	files = [],
	imageUrls = [],
	onImageUrlsChange,
	onRemoveImage,
}: ProductImagesCardProps) => {
	const addMoreRef = useRef<HTMLInputElement>(null);
	const [current, setCurrent] = useState(0);
	const [showPhotos, setShowPhotos] = useState(false);
	const [isDragging, setIsDragging] = useState(false);

	const isEditable = mode !== 'view';
	const hasImages = imageUrls.length > 0;

	const showCarousel = hasImages && (showPhotos || mode === 'view');
	const showUploadZone = isEditable && showPhotos && !hasImages;
	const show3D = !showCarousel && !showUploadZone;

	const handleFiles = (fileList: FileList | null) => {
		if (!fileList || !fileList.length) return;

		const selectedFiles = Array.from(fileList);
		const urls = selectedFiles.map((file) => URL.createObjectURL(file));

		onImageUrlsChange?.([...imageUrls, ...urls]);
		onFilesChange?.([...files, ...selectedFiles]);
	};

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		handleFiles(e.target.files);
		e.target.value = '';
	};

	const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
		e.preventDefault();
		setIsDragging(false);
		handleFiles(e.dataTransfer.files);
	};

	const handleRemoveCurrent = async () => {
		const url = imageUrls[current];

		if (url && !url.startsWith('blob:')) {
			try {
				await onRemoveImage?.(url);
			} catch (error) {
				return;
			}
		}

		if (url?.startsWith('blob:')) {
			URL.revokeObjectURL(url);

			const blobIndex =
				imageUrls.slice(0, current + 1).filter((image) => image.startsWith('blob:')).length - 1;

			const nextFiles = files.filter((_, index) => index !== blobIndex);
			onFilesChange?.(nextFiles);
		}

		const nextImages = imageUrls.filter((_, index) => index !== current);

		onImageUrlsChange?.(nextImages);
		setCurrent(Math.min(current, nextImages.length - 1));

		if (!nextImages.length) setShowPhotos(false);
	};

	return (
		<FormCard icon={<ImageIcon size={16} />} title="Fotos do Produto" className={className}>
			{isEditable && (
				<input
					ref={addMoreRef}
					type="file"
					accept="image/*"
					multiple
					className={styles.hiddenInput}
					onChange={handleFileChange}
				/>
			)}

			{showCarousel && (
				<ProductPhotoCarousel images={imageUrls} current={current} onCurrentChange={setCurrent} />
			)}

			{showUploadZone && (
				<label
					className={`${styles.uploadZone} ${isDragging ? styles.dragging : ''}`}
					onDragOver={(e) => {
						e.preventDefault();
						setIsDragging(true);
					}}
					onDragLeave={() => setIsDragging(false)}
					onDrop={handleDrop}
				>
					<input
						type="file"
						accept="image/*"
						multiple
						className={styles.hiddenInput}
						onChange={handleFileChange}
					/>
					<FileUploadOutlined className={styles.uploadIcon} />
					<span className={styles.uploadTitle}>Arraste ou clique para enviar fotos</span>
					<span className={styles.uploadSubtitle}>Formatos aceitos: JPG, PNG, WEBP</span>
				</label>
			)}

			{show3D && (
				<div className={styles.model3DWrapper}>
					{preview3DSlot ?? <div className={styles.placeholder} />}
				</div>
			)}

			{isEditable && (
				<div className={styles.actions}>
					{!showPhotos ? (
						<ActionButton text="Usar fotos do produto" onClick={() => setShowPhotos(true)} />
					) : (
						<div className={styles.buttons}>
							{' '}
							{hasImages && (
								<div className={styles.rowActionButtons}>
									<RowActionButton variant="add" onClick={() => addMoreRef.current?.click()} />
									<RowActionButton variant="delete" onClick={handleRemoveCurrent} />
								</div>
							)}
							<div className={styles.actionButton}>
								<ActionButton text="Usar modelo 3D" onClick={() => setShowPhotos(false)} />
							</div>
						</div>
					)}
				</div>
			)}
		</FormCard>
	);
};
